"use client";
import image from "@/constants/image";
import { APP_ROUTES } from "@/constants/routes";
import { useAppSelector } from "@/lib/hooks";
import { Category } from "@/models/category";
import { langSupports } from "@/models/general";
import { User } from "@/models/user";
import { Menu, MenuItem, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import { useLocale } from "next-intl";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import classes from "../styles.module.scss";
interface Props {
  locale?: string;
  user?: User | null;
}

const ChangeLanguage = ({ locale, user }: Props) => {
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { currentBlogSLug } = useAppSelector((state) => state.post);
  const categories = queryClient.getQueryData<Category[]>([
    "categories-wp",
    { language: locale },
  ]);
  const categoriesMap = useMemo(() => {
    return new Map<string, Category>(
      categories?.map((cat) => {
        const langCat = cat.languages.find((it) => it.language === locale);
        if (langCat) {
          return [langCat.slug, cat];
        }
        return [cat.slug, cat];
      }) || []
    );
  }, [categories]);
  const localeclient = useLocale();
  const [anchorElLang, setAnchorElLang] = useState<null | HTMLElement>(null);
  const router = useRouter();
  const changeLanguage = async (lang: string) => {
    if (lang === locale) return;
    document.cookie = `lang=${lang}; path=/; max-age=${365 * 24 * 60 * 60}`;
    const newPathname = pathname.replace(`/${locale}`, `/${lang}`);
    const queryString = window.location.search;
    if (pathname.includes("/category/")) {
      const currentSlug = pathname.split("/category/")[1].split("/")[0];
      const categorySlug = categoriesMap.get(currentSlug);
      if (categorySlug) {
        const langCat = categorySlug.languages.find(
          (it) => it.language === lang
        );
        if (langCat) {
          router.replace(
            APP_ROUTES(lang).RESOURCES.to(langCat.slug) + queryString
          );
          return;
        }
        router.replace(
          APP_ROUTES(lang).RESOURCES.to(categorySlug.slug) + queryString
        );
        return;
      }
    }
    if (pathname.includes("/blog/")) {
      if (Object.keys(currentBlogSLug).length > 0) {
        const slug = currentBlogSLug[lang];
        if (slug) {
          router.replace(APP_ROUTES(lang).BLOG_DETAIL.to(slug));
          return;
        }
      }
    }
    router.replace(newPathname + queryString);
  };

  const handleLanguage = useMemo(() => {
    let effectiveLang = locale;
    if (effectiveLang !== user?.language) {
      effectiveLang = locale;
    }
    switch (effectiveLang) {
      case "vi":
        return (
          <Image
            src={image.iconVietNam}
            alt={"Vietnam"}
            width={36}
            height={24}
            className="!h-full object-cover aspect-4/3"
          />
        );
      case "id":
        return (
          <Image
            src={image.iconID}
            alt={"Indonesia"}
            width={36}
            height={24}
            className="!h-full object-cover aspect-4/3"
          />
        );
      default:
        return (
          <Image
            src={image.iconEN}
            alt={"English"}
            width={36}
            height={24}
            className="!h-full object-cover aspect-4/3"
          />
        );
    }
  }, [locale, user?.language]);
  return (
    <>
      <button
        type="button"
        onClick={(e) => setAnchorElLang(e.currentTarget)}
        className="p-0 border border-solid border-[#adb5bc] cursor-pointer h-6"
      >
        {handleLanguage}
      </button>
      <Menu
        disableScrollLock={true}
        anchorEl={anchorElLang}
        open={Boolean(anchorElLang)}
        onClose={() => setAnchorElLang(null)}
        classes={{
          paper: clsx("min-w-[130px]"),
        }}
      >
        {langSupports.map((it) => (
          <MenuItem
            key={it.key}
            onClick={() => changeLanguage(it.key)}
            className={clsx("m-0 flex gap-2 justify-start items-center", {
              "!hidden": it.key === "id",
            })}
            selected={it.key === localeclient}
          >
            <Image
              src={it.img}
              alt={it.name}
              width={36}
              height={24}
              priority
              quality={100}
              className={clsx("!object-cover aspect-4/3")}
            />
            <Typography className={classes.itName}>{it.name}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ChangeLanguage;
